import React, { forwardRef, useCallback, useMemo } from 'react';
import { Table } from '../../../../../components/PANWDSElements';
import { ColumnOptions, RowInstance } from '@panwds/react-table';
import { useTranslate } from "../../../../../customHooks";
import { Tooltip, TruncateText } from '@panwds/react-ui';
import { DeleteIcon } from '@panwds/icons';
import { FirewallPrivateAccessTableProps, PrivateAccessConfig } from './types';
import { PrivateAccessType } from '../../../../../interfaces/interfaces';

const FirewallPrivateAccessTable = forwardRef<HTMLInputElement, FirewallPrivateAccessTableProps>(
    ({ ...props }, ref) => {
    const {
        value,
        deletePrivateAccess,
        setEditPrivateAccessData,
        disabled = false,
    } = props;

    const translate = useTranslate();

    const getResourceTypeDisplayName = useCallback((type: PrivateAccessType): string => {
        const typeMap: Record<PrivateAccessType, string> = {
            USERID: translate('resources.firewallsV2.privateAccess.userIdRedistributionAgent'),
            LDAP: translate('resources.firewallsV2.privateAccess.lDAPProfile'),
            DNS: translate('resources.firewallsV2.privateAccess.dnsResolver'),
        };
        return typeMap[type] || type;
    }, [translate]);

    const columns = useMemo<ColumnOptions<PrivateAccessConfig>[]>(() => [{
        accessor: 'Name',
        Header: translate('generic.name'),
        Cell: ({ row }: { row: RowInstance<PrivateAccessConfig> }) => {
            return <span
                className={disabled ? 'tw-text-gray-600' : 'tw-text-blue-600 tw-hover:text-blue-600 tw-cursor-pointer'}
                onClick={disabled ? undefined : () => setEditPrivateAccessData(row?.original)}
            >{row?.original?.Name}</span>
        }
    }, {
      accessor: 'Status',
      Header: translate('resources.firewallsV2.privateAccess.connectionStatus'),
    }, {
        accessor: 'Type',
        Header: translate(`resources.firewallsV2.privateAccess.resourceType`),
        Cell: ({ value }: { value: PrivateAccessType }) => {
          return getResourceTypeDisplayName(value);
        },
    },{
      accessor: 'ResourceArn',
      Header: translate('resources.firewallsV2.privateAccess.AWSResourceId'),
      Cell: ({ value }: { value: string }) => {
        return <TruncateText>{value}</TruncateText>
      },
    },{
        accessor: 'actions',
        Header: translate(`generic.actions`),
        Cell: ({ row }: { row: RowInstance<PrivateAccessConfig> }) => {
          return (
            <>
              <Tooltip label={translate(`generic.delete`)}>
                <button
                  type="button"
                  className="tw-mr-2 tw-border-none tw-bg-transparent"
                  onClick={() => {
                    deletePrivateAccess(row?.original);
                  }}
                  disabled={disabled}
                >
                  <DeleteIcon
                    className={disabled ? "tw-text-gray-300 tw-cursor-not-allowed" : "tw-text-zinc-400 dark:tw-text-gray-600 dark:group-hover:tw-text-gray-400 tw-cursor-pointer"}
                    size="sm"
                    data-testid="firewall-edit-endpoint-delete"
                  />
                </button>
              </Tooltip>
            </>
          );
        },
        sticky: true,
      },], [setEditPrivateAccessData, deletePrivateAccess, translate, disabled, getResourceTypeDisplayName]);

    return (
    <div>
        <Table
            tableComponentData={value || []}
            tableComponentColumns={columns}
            tableComponentEmptyStateProps={{
                heading: translate('resources.firewallsV2.privateAccess.emptyPrivateAccessTable'),
            }}
            tableBatchAction={undefined}
            tableComponentFilterBar={{
                hideAddFilter: true,
                filterConfig: undefined,
            }}
            tableTitle={""}
            />
    </div>
  );
    });

FirewallPrivateAccessTable.displayName = 'FirewallPrivateAccessTable';

export default FirewallPrivateAccessTable;
